import { ReduxState, RequestWithdrawalResult, TransactionState, WithdrawalResult } from 'interfaces';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Badge, Button, Modal } from '../../../../components';
import { connect, ConnectedProps } from 'react-redux';
import OtpInput from '../../../../components/otpInput/otpInput.index';
import { requestWithdrawal, verifyWithdrawalRequest } from '../irtWithdrawal.service';
import { LoadingOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import withdrawalFailure from 'assets/images/withdrawal-failure.png';
import withdrawalSuccess from 'assets/images/withdrawal-success.png';
import { copyToClipboard } from '../../../../../scripts';
import { TbCopy } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

interface OwnProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    requestWithdrawalResult: RequestWithdrawalResult;
    amount: number;
    bankAccountId: string;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

enum Stage {
    Initial,
    Result,
}

const WithdrawalModal: FC<Props> = ({ open, setOpen, requestWithdrawalResult, amount, bankAccountId, user }) => {
    const [stage, setStage] = useState<Stage>(Stage.Initial);
    const [remainingTime, setRemainingTime] = useState<number>(120);
    const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
    const [resendCodeLoading, setResendCodeLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');
    const [withdrawalResult, setWithdrawalResult] = useState<WithdrawalResult | undefined>();

    const navigate = useNavigate();

    const resetFunction = () => {
        setStage(Stage.Initial);
        setRemainingTime(120);
        setIsTimerActive(true);
        setResendCodeLoading(false);
        setLoading(false);
        setCode('');
        setWithdrawalResult(undefined);
    };

    const renderTitle = () => {
        switch (stage) {
            case Stage.Initial:
                return 'جزئیات برداشت تومانی';
            default:
                return undefined;
        }
    };

    const handleResendClick = () => {
        const onComplete = () => {
            setCode('');
            setRemainingTime(120);
            setIsTimerActive(true);
        };

        requestWithdrawal(
            setResendCodeLoading,
            { amount, IBanNumberId: bankAccountId, platform: 1 },
            undefined,
            onComplete,
        );
    };

    const onSubmit = () => {
        if (code?.length === 6) {
            const onComplete = () => setStage(Stage.Result);
            verifyWithdrawalRequest(
                setLoading,
                {
                    trackingCode: requestWithdrawalResult.tracking,
                    otp: code,
                    platformType: 1,
                },
                setWithdrawalResult,
                onComplete,
            );
        }
    };

    const isSuccess = (): boolean => {
        switch (withdrawalResult?.state?.id) {
            case TransactionState.Failed:
            case TransactionState.Rejected:
                return false;
            default:
                return true;
        }
    };

    useEffect(() => {
        if (isTimerActive && remainingTime > 0) {
            const timer = setInterval(() => {
                setRemainingTime((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (remainingTime === 0) {
            setIsTimerActive(false);
        }
    }, [isTimerActive, remainingTime]);

    useEffect(() => {
        if (!open) {
            resetFunction();
        }
    }, [open]);

    return (
        <Modal open={open} setOpen={(value) => setOpen(value)} title={renderTitle()} closable={stage === Stage.Initial}>
            {stage === Stage.Initial && (
                <>
                    <Divider />
                    <div className={'flex justify-between items-center mt-6'}>
                        <span className={'text-base font-medium text-black-60'}>مقدار برداشت:</span>
                        <span className={'text-base font-medium text-black-100'}>{amount.toLocaleString()} تومان</span>
                    </div>
                    <div className={'flex justify-between items-center mt-4'}>
                        <span className={'text-base font-medium text-black-60'}>کارمزد:</span>
                        <span className={'text-base font-medium text-black-100'}>
                            {requestWithdrawalResult.withdrawalCommission.toLocaleString()} تومان
                        </span>
                    </div>
                    <div className={'flex justify-between items-center mt-4'}>
                        <span className={'text-base font-medium text-black-60'}>شناسه برداشت:</span>
                        <span className={'flex items-center gap-1 text-base font-medium text-black-100 en'}>
                            {requestWithdrawalResult.tracking}
                            <TbCopy
                                className={'clickable'}
                                onClick={() => copyToClipboard(requestWithdrawalResult?.tracking, 'شناسه برداشت')}
                            />
                        </span>
                    </div>
                    <span className={'flex text-sm font-medium mt-6'}>
                        کد پیامک شده به شماره {user?.phoneNumber} را وارد کنید
                    </span>
                    <div className={'flex items-center justify-between mt-4 mb-2'}>
                        <span className={'text-base font-bold'}>کد پیامک شده:</span>
                        {remainingTime === 0 ? (
                            <p
                                className={`flex text-base items-center gap-2 font-bold clickable ${resendCodeLoading ? '!opacity-50' : ''}`}
                                onClick={handleResendClick}
                            >
                                ارسال مجدد کد
                                {resendCodeLoading ? <LoadingOutlined /> : null}
                            </p>
                        ) : (
                            <p className={'font-medium'}>
                                <span className={'font-bold'}>{remainingTime} ثانیه </span>تا ارسال مجدد کد
                            </p>
                        )}
                    </div>
                    <OtpInput length={6} onChange={(otp) => setCode(otp)} />
                    <div className={'flex items-center justify-between gap-4 mt-6'}>
                        <Button
                            type={'primary'}
                            className={'w-full'}
                            loading={loading}
                            disabled={loading || code?.length < 6}
                            onClick={onSubmit}
                        >
                            ثبت برداشت
                        </Button>
                        <Button
                            type={'text'}
                            className={'bg-background w-full'}
                            onClick={() => {
                                resetFunction();
                                setOpen(false);
                            }}
                        >
                            انصراف
                        </Button>
                    </div>
                </>
            )}
            {stage === Stage.Result && withdrawalResult && (
                <>
                    <img
                        src={isSuccess() ? withdrawalSuccess : withdrawalFailure}
                        alt={'Result'}
                        className={'mt-12 mx-auto max-lg:mt-8 max-lg:max-w-[216px]'}
                    />
                    <div className={'flex flex-col items-center mx-auto gap-2 mt-8'}>
                        <span
                            className={`font-bold text-xl max-lg:text-base ${isSuccess() ? 'text-positive-dark' : 'text-negative-dark'}`}
                        >
                            {isSuccess() ? 'برداشت با موفقیت ثبت شد!' : 'برداشت ناموفق بود!'}
                        </span>
                        <span className={'text-base font-medium max-lg:text-xs'}>
                            {isSuccess()
                                ? 'سفارش برداشت شما موفق بود.'
                                : 'سفارش برداشت شما ناموفق بود٬ لطفا دوباره تلاش کنید.'}
                        </span>
                    </div>
                    <div className={'flex justify-between items-center mt-8'}>
                        <span className={'text-base font-medium text-black-60 max-lg:text-sm'}>وضعیت:</span>
                        <Badge type={isSuccess() ? 'success' : 'danger'} title={withdrawalResult?.state?.title} />
                    </div>
                    <div className={'flex justify-between items-center mt-4'}>
                        <span className={'text-base font-medium text-black-60 max-lg:text-sm'}>مقدار برداشت:</span>
                        <span className={'text-base font-medium text-black-100 max-lg:text-sm'}>
                            {Math.abs( withdrawalResult?.amount).toLocaleString()} تومان
                        </span>
                    </div>
                    <div className={'flex justify-between items-center mt-4'}>
                        <span className={'text-base font-medium text-black-60 max-lg:text-sm'}>شناسه برداشت:</span>
                        <span
                            className={
                                'text-base font-medium en text-black-100 en flex gap-2 items-center max-lg:text-sm'
                            }
                        >
                            {withdrawalResult?.trackingCode}
                            <TbCopy
                                className={'clickable'}
                                onClick={() => copyToClipboard(withdrawalResult?.trackingCode, 'شناسه برداشت')}
                            />
                        </span>
                    </div>
                    <div className={'flex justify-between items-center mt-6 gap-4'}>
                        <Button type={'primary'} className={'w-full'} onClick={() => navigate('/reports')}>
                            صفحه گزارشات
                        </Button>
                        <Button
                            className={'bg-background w-full'}
                            onClick={() => {
                                resetFunction();
                                setOpen(false);
                            }}
                        >
                            بستن
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    user: state.userData,
});

const connector = connect(mapStateToProps);
export default connector(WithdrawalModal);
